import {
    Box,
    Caption,
    Info,
    LinkText,
    Status,
    StatusBadge,
    TextBlock,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import { TFunction } from "i18next";
import React from "react";
import { Column } from "react-table";

import ConnectedWorkflowsList from "./ConnectedWorkflowsList";
import ProductNameColumn from "./ProductNameColumn";
import { isNewerEditorAvailable, LatestEditorVersions } from "./WorkflowUtil";
import { LockIconBig } from "components/icons/LockIconBig";
import DateCell from "components/table/DateCell";
import { deriveColumnWidth } from "components/table/Table";
import { ActiveWorkflowUserList } from "components/workflows/ActiveWorkflowUserList";
import styles from "components/workflows/all-workflows-table.scss";
import { InitialTab } from "components/workflows/workflow-editor-v3/WorkflowEditorV3View";
import { WorkflowsTableKebabMenu } from "components/workflows/WorkflowsTableKebabMenu";
import {
    AUTH_WORKFLOW_ENABLE,
    AUTH_WORKFLOW_LAUNCH,
    AUTH_WORKFLOW_SCHEDULE,
    AUTH_WORKFLOW_SECURED,
} from "domain/authority";
import { ConnectedWorkflows, LaunchType, ScheduleStatus, WorkflowTableData } from "domain/workflows";
import { hasTenantCookie, isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { Profile, WorkflowEditorV3SupportedEntry, WorkflowService } from "services/workflows/WorkflowService";

import testIds from "testIds.json";

interface Props {
    t: TFunction;
    tableContainerRef: React.RefObject<HTMLDivElement>;
    workflowService: WorkflowService;
    latestEditorVersions?: LatestEditorVersions;
    workflowEditorV3Supported: WorkflowEditorV3SupportedEntry[];
    onUpdate: () => void;
    openWithV3: (initialTabs: InitialTab[]) => void;
    showManageDialog: (uuid: string, version: string, product: string, connectedWorkflows: ConnectedWorkflows) => void;
    setIsSidePanelOpen: (isOpen: boolean) => void;
    setSelectedRow: React.Dispatch<React.SetStateAction<WorkflowTableData | undefined>>;
}

export const createAllWorkflowsTableColumns = (props: Props) => {
    const { t } = props;

    const columns: Array<Column<WorkflowTableData>> = [
        {
            Header: t("WorkflowsTable.name"),
            accessor: "name",
            Cell: ({ row, value }) => {
                const workflow = row.original;

                const isBmpSecured = workflow.profile === Profile.BMP && workflow.secured;
                const hasSecuredAuth = userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SECURED]);
                const isRestrictedSecured = isBmpSecured && (!isCurrentUserInternal() || !hasSecuredAuth);

                const canLaunchWorkflow =
                    userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_LAUNCH]) &&
                    workflow.launchType === LaunchType.MANUAL &&
                    workflow.profile === Profile.BMP;

                const canScheduleWorkflow =
                    userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SCHEDULE]) &&
                    workflow.launchType === LaunchType.MANUAL &&
                    workflow.profile === Profile.BMP;

                const canEnableWorkflow =
                    userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_ENABLE]) &&
                    workflow.launchType !== LaunchType.MANUAL &&
                    workflow.profile === Profile.BMP &&
                    !hasTenantCookie();

                return (
                    <TextCell>
                        <Box
                            verticalAlignment="Top"
                            gap="Xxs"
                            className={styles.nameCellContent}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {(!isRestrictedSecured ||
                                canLaunchWorkflow ||
                                canScheduleWorkflow ||
                                canEnableWorkflow) && (
                                <WorkflowsTableKebabMenu
                                    onUpdate={props.onUpdate}
                                    workflowService={props.workflowService}
                                    onOpenWithV3={props.openWithV3}
                                    updateVisible={isNewerEditorAvailable(
                                        workflow.profile,
                                        workflow.editorGeneration,
                                        workflow.editorVersion,
                                        props.latestEditorVersions,
                                        props.workflowEditorV3Supported
                                    )}
                                    setIsSidePanelOpen={props.setIsSidePanelOpen}
                                    setSelectedRow={props.setSelectedRow}
                                    workflow={workflow}
                                />
                            )}

                            {isRestrictedSecured ? (
                                <Box verticalAlignment="Center" gap="Xxs" className={styles.upperRow}>
                                    {isBmpSecured && (
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <LockIconBig color={"var(--theme-icon-fill-grey)"} size={20} />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <TextBlock disableBottomSpacing>
                                                    {t("WorkflowsTable.setWorkflowSecured.tooltip")}
                                                </TextBlock>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                    <strong>{value}</strong>
                                </Box>
                            ) : (
                                <Box verticalAlignment="Top" gap="Xxxs">
                                    {isBmpSecured && (
                                        <Box verticalAlignment="Center" className={styles.upperRow}>
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    <LockIconBig color={"var(--theme-icon-fill-grey)"} size={20} />
                                                </TooltipTrigger>
                                                <TooltipContent>{t("Common.secured")}</TooltipContent>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Box columnLayout>
                                        <Box verticalAlignment="Center" className={styles.upperRow}>
                                            <LinkText
                                                as="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    props.showManageDialog(
                                                        workflow.uuid,
                                                        workflow.editorVersion,
                                                        workflow.profile,
                                                        workflow.connectedWorkflows
                                                    );
                                                }}
                                                className={styles.linkText}
                                            >
                                                {value}
                                            </LinkText>
                                        </Box>

                                        {workflow.assetGrading && (
                                            <Caption greyTextStyle>{t("workflowTemplate.assetGrading")}</Caption>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </TextCell>
                );
            },
            width: deriveColumnWidth(30, props.tableContainerRef),
        },
        {
            Header: t("WorkflowsTable.product"),
            accessor: "product",
            Cell: ({ cell: { row } }) => (
                <TextCell>
                    <ProductNameColumn
                        profile={row.original.product.toLowerCase()}
                        version={row.original.editorVersion}
                        generation={row.original.editorGeneration}
                        t={t}
                    />
                </TextCell>
            ),
            width: deriveColumnWidth(30, props.tableContainerRef),
        },
        {
            Header: (
                <Box verticalAlignment="Center" gap="Xxs">
                    {t("Common.status")}
                    <Tooltip placement="top">
                        <TooltipTrigger>
                            <Info size={20} />
                        </TooltipTrigger>
                        <TooltipContent>{t("WorkflowsTable.statusTooltip")}</TooltipContent>
                    </Tooltip>
                </Box>
            ),
            accessor: "defaultWorkflow",
            id: "defaultWorkflow.other.usernames",
            Cell: ({ cell: { value, row } }) => {
                const own = value.own;
                const usernames = value.other.userDetails;
                return row.original.launchType != LaunchType.MANUAL ? (
                    <TextCell>
                        <Box verticalAlignment="Center" onClick={(e) => e.stopPropagation()}>
                            {row.original.profile === Profile.BMP ? (
                                row.original.enabled ? (
                                    <StatusBadge
                                        value={t("Common.active")}
                                        status={Status.SUCCESS}
                                        dataType={"ACTIVE"}
                                    />
                                ) : (
                                    <StatusBadge
                                        value={t("Common.inactive")}
                                        status={Status.NEUTRAL}
                                        dataType={"INACTIVE"}
                                    />
                                )
                            ) : (
                                own && (
                                    <StatusBadge
                                        value={t("WorkflowsTable.default")}
                                        status={Status.SUCCESS}
                                        dataType={"ACTIVE"}
                                    />
                                )
                            )}

                            {row.original.profile !== Profile.BMP && usernames.length > 0 && (
                                <ActiveWorkflowUserList
                                    userDetails={usernames}
                                    own={own}
                                    userCount={value.other.count}
                                />
                            )}
                        </Box>
                    </TextCell>
                ) : (
                    <>
                        {row.original.schedule !== undefined && row.original.schedule !== ScheduleStatus.NONE && (
                            <StatusBadge
                                value={t("Common.scheduled")}
                                status={
                                    row.original.schedule === ScheduleStatus.ACTIVE ? Status.SUCCESS : Status.NEUTRAL
                                }
                                dataType={
                                    row.original.schedule === ScheduleStatus.ACTIVE
                                        ? ScheduleStatus.ACTIVE
                                        : ScheduleStatus.INACTIVE
                                }
                                testId={testIds.workArea.workflows.table.scheduleBadge}
                            />
                        )}
                    </>
                );
            },
            width: deriveColumnWidth(25, props.tableContainerRef),
        },
        {
            Header: t("WorkflowsTable.connectedWorkflows.title"),
            accessor: "connectedWorkflows",
            Cell: ({ cell: { value } }) => {
                return value.totalConnectedWorkflows ? (
                    <ConnectedWorkflowsList connectedWorkflows={value} isSubWorkflow={false} />
                ) : null;
            },
            width: deriveColumnWidth(25, props.tableContainerRef),
        },
        {
            Header: t("WorkflowsTable.createdDate"),
            accessor: "createdDate",
            Cell: ({ cell: { value } }) => (
                <TextCell>
                    <DateCell value={value} />
                </TextCell>
            ),
            width: deriveColumnWidth(20, props.tableContainerRef),
        },
        {
            Header: t("WorkflowsTable.creator"),
            accessor: "creator",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: deriveColumnWidth(35, props.tableContainerRef),
        },
    ];

    return columns;
};
